import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDeliveries, list, bolPublicUrl } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { StatusPill } from "@/components/StatusPill";
import { ExportButtons } from "@/components/ExportButtons";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FileText, MapPin, PackageCheck, Image as ImageIcon, PenLine, X } from "lucide-react";

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "");
const fmtTime = (v) => (v ? String(v).slice(11, 16) : "");
const dayOf = (v) => (v ? String(v).slice(0, 10) : null);

export default function Deliveries() {
  const { data: rows = [] } = useQuery({ queryKey: ["deliveries"], queryFn: getDeliveries });
  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: () => list("drivers") });
  const [driverId, setDriverId] = useState("all");
  const [date, setDate] = useState("");
  const [preview, setPreview] = useState(null); // {src, label}

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (driverId !== "all" && r.driver_id !== driverId) return false;
      if (date && dayOf(r.pod_at) !== date) return false;
      return true;
    });
  }, [rows, driverId, date]);

  const Thumb = ({ src, label, icon: Icon }) =>
    src ? (
      <button type="button" data-testid="delivery-thumb" onClick={() => setPreview({ src, label })}
        className="group relative h-14 w-20 rounded border border-border/60 overflow-hidden bg-neutral-50 hover:ring-2 hover:ring-[#0055FF]/40 transition-shadow">
        <img src={src} alt={label} className="h-full w-full object-contain" />
      </button>
    ) : (
      <div className="h-14 w-20 rounded border border-dashed border-neutral-300 grid place-items-center text-neutral-300">
        <Icon className="h-4 w-4" />
      </div>
    );

  return (
    <Layout>
      <PageHeader title="Deliveries" subtitle="Completed proof-of-delivery audit trail — signatures, photos & signed BOLs">
        <ExportButtons collection="deliveries" />
      </PageHeader>

      <div className="p-8 space-y-6 animate-fade-up">
        <Card className="p-4 border-border/60 shadow-none">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Driver</Label>
              <Select value={driverId} onValueChange={setDriverId}>
                <SelectTrigger className="h-9 mt-1.5" data-testid="deliveries-driver-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All drivers</SelectItem>
                  {drivers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.driver_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Delivered On</Label>
              <Input type="date" data-testid="deliveries-date-filter" className="h-9 mt-1.5" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="flex items-center gap-3">
              {(driverId !== "all" || date) && (
                <Button variant="outline" size="sm" data-testid="deliveries-clear-filters" className="border-border/60"
                  onClick={() => { setDriverId("all"); setDate(""); }}>Clear</Button>
              )}
              <span className="text-sm text-muted-foreground" data-testid="deliveries-count">{filtered.length} delivery{filtered.length === 1 ? "" : "s"}</span>
            </div>
          </div>
        </Card>

        {filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="deliveries-empty">
            <PackageCheck className="h-10 w-10 mx-auto mb-3 text-neutral-300" />
            No completed deliveries match your filters yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map((r) => (
              <Card key={r.id} data-testid={`delivery-card-${r.id}`} className="p-4 border-border/60">
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono-data font-semibold text-[#0055FF]">{r.work_order_no || r.requisition_no}</span>
                    <span className="text-sm font-medium truncate">{r.project_name}</span>
                  </div>
                  <StatusPill value={r.status} />
                </div>
                <div className="space-y-1.5 text-sm text-neutral-700">
                  <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-neutral-400 mt-0.5 shrink-0" />{r.pickup_location || "?"} → {r.delivery_location || "?"}</div>
                  <div className="text-[13px] text-neutral-600">
                    <b>{r.driver_name || "—"}</b> · received by <b>{r.pod_signed_by}</b> on {fmtDate(r.pod_at)} {fmtTime(r.pod_at)}
                  </div>
                  {r.pod_notes && <div className="text-xs text-neutral-500 italic">"{r.pod_notes}"</div>}
                </div>
                <div className="flex items-end justify-between gap-3 mt-3">
                  <div className="flex gap-3">
                    <div className="space-y-1"><div className="text-[9px] uppercase tracking-wide text-neutral-400 font-semibold">Signature</div><Thumb src={r.pod_signature} label="Signature" icon={PenLine} /></div>
                    <div className="space-y-1"><div className="text-[9px] uppercase tracking-wide text-neutral-400 font-semibold">Photo</div><Thumb src={r.pod_photo} label="Delivery Photo" icon={ImageIcon} /></div>
                  </div>
                  <a href={bolPublicUrl(r.id, r.bol_token)} target="_blank" rel="noopener noreferrer"
                    data-testid={`delivery-bol-${r.id}`} className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-medium transition-colors">
                    <FileText className="h-4 w-4" /> Signed BOL
                  </a>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{preview?.label}</DialogTitle></DialogHeader>
          {preview && <img src={preview.src} alt={preview.label} className="w-full max-h-[70vh] object-contain rounded border border-border/60 bg-neutral-50" data-testid="delivery-preview-img" />}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
